import api from '../api';
import { API_URL } from '../config';

/**
 * Fetches monthly salary report data
 * @param {string} from - Start date in 'YYYY-MM-DD' format
 * @param {string} to - End date in 'YYYY-MM-DD' format
 * @returns {Promise<Array>} - List of employee salary reports
 */
export const getSalaryReport = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/salary`, {
      params: { from, to }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching salary report:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to fetch salary report. Please check the date range and try again.'
    );
  }
};

/**
 * Fetches offboarded employee salary total report data
 * @param {string} from - Start date in 'YYYY-MM-DD' format
 * @param {string} to - End date in 'YYYY-MM-DD' format
 * @returns {Promise<Array>} - List of offboarded employee salary reports
 */
export const getOffboardedSalaryReport = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/salary/offboarded`, {
      params: { from, to }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching offboarded salary report:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to fetch offboarded salary report. Please check the date range and try again.'
    );
  }
};

/**
 * Fetches attendance report (leaves + absences) for a given date range
 * @param {string} from - Start date in 'YYYY-MM-DD' format
 * @param {string} to - End date in 'YYYY-MM-DD' format
 * @returns {Promise<Array>} - List of employee attendance reports
 */
export const getAttendanceReport = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/attendance`, {
      params: { from, to }
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching attendance report:', {
      status: error.response?.status || 'N/A',
      error: error.response?.data?.error || 'Unknown error',
      message: error.response?.data?.message || error.message,
      exceptionType: error.response?.data?.exceptionType || 'N/A',
      path: error.response?.data?.path || 'N/A',
      timestamp: error.response?.data?.timestamp || 'N/A'
    });

    throw new Error(
      error.response?.data?.message ||
      'Failed to fetch attendance report. Please check the date range and try again.'
    );
  }
};

// Fetches summary statistics for attendance within a date range
export const getAttendanceSummary = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/attendance/summary`, {
      params: { from, to }
    });
    return res.data; // returns plain object like { sicknessRecords: 12, ... }
  } catch (error) {
    console.error('Error fetching attendance summary:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to load attendance summary.'
    );
  }
};

// Fetches summary statistics for attendance within a date range
export const getOperationalHours = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/operational_hours`, {
      params: { from, to }
    });
    return res.data; // returns plain object like { sicknessRecords: 12, ... }
  } catch (error) {
    console.error('Error Operational Hours summary:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to Operational Hours summary.'
    );
  }
};

// Fetches employees with high absence records within a date range
export const getEmployeesWithHighAbsence = async (from, to) => {
  try {
    const res = await api.get(`${API_URL}/reporting/attendance/high-absence`, {
      params: { from, to }
    });
    return res.data; // { latenessEmployees: [...], personalEmployees: [...], sicknessEmployees: [...] }
  } catch (error) {
    console.error('Error fetching high-absence employees:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to load employees with high absence records.'
    );
  }
};

// Attendance summary for the current year
export const getYearlyAttendanceSummary = async (year = new Date().getFullYear()) => {
  try {
    const res = await api.get(`${API_URL}/reporting/attendance/yearly`, {
      params: { year }
    });
    return res.data;
  } catch (error) {
    throw new Error('Failed to load yearly attendance summary.');
  }
};

// Employee Report
export const getEmployeeAttendanceReport = async (employeeId, endDate) => {
  try {
    const url = `${API_URL}/reporting/attendance/employee/${employeeId}`;
    const params = endDate ? { endDate } : {};
    const res = await api.get(url, { params });
    return res.data;
  } catch (error) {
    throw new Error('Failed to load employee attendance report.');
  }
};

/**
 * Fetches final pay statement for a specific employee
 * @param {string} employeeId - The employee's ID
 * @param {string} from - Start date (optional)
 * @param {string} to - End date (optional)
 * @returns {Promise<Object>} - The final pay statement data
 */
export const getFinalPayStatement = async (employeeId, from, to) => {
  try {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const res = await api.get(`${API_URL}/reporting/final-pay/employee/${employeeId}`, {
      params
    });
    return res.data;
  } catch (error) {
    console.error('Error fetching final pay statement:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to fetch final pay statement.'
    );
  }
};

const formatDateToYYYYMMDD = (dateVal) => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
    return dateVal;
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export async function getRecruitmentReport(startDate, endDate) {
  try {
    const formattedFrom = formatDateToYYYYMMDD(startDate);
    const formattedTo = formatDateToYYYYMMDD(endDate);
    console.log('--------------------------------------------------');
    console.log('[FRONTEND API] Raw Input Dates:', { startDate, endDate });
    console.log('[FRONTEND API] Formatted Params Sent to Backend:', { from: formattedFrom, to: formattedTo });
    console.log('--------------------------------------------------');
    const response = await api.get(`${API_URL}/reporting/recruitment`, {
      params: { from: formattedFrom, to: formattedTo }
    });
    console.log('[FRONTEND API] Received Response Data:', response.data);
    return response.data;
  } catch (error) {
    console.error('[FRONTEND API] Error fetching recruitment report:', error);
    throw error;
  }
}

export async function getCandidateDetailsReport() {
  try {
    const response = await api.get(`${API_URL}/candidates/report`);
    return response.data;
  } catch (error) {
    console.error('Error fetching candidate details report:', error);
    throw new Error(
      error.response?.data?.message ||
      'Failed to fetch candidate details report.'
    );
  }
}

